import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../../firebase/config";
import { doc, onSnapshot, updateDoc, collection, addDoc, query, orderBy, getDocs, Timestamp } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

const ESTADOS_LIQ = ['Pendiente presentación', 'Presentado', 'Cobrado'];

export default function DetallePrestador() {
  const { id } = useParams();
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [prestador, setPrestador] = useState(null);
  const [editando, setEditando] = useState(false);
  const [datos, setDatos] = useState({});
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [modalLiquidacion, setModalLiquidacion] = useState(false);
  const [nuevaLiq, setNuevaLiq] = useState({ periodo: '', cantidadTurnos: '', montoARS: '', montoUSD: '', fechaPresentacion: '', fechaEstimadaCobro: '', estado: 'Pendiente presentación', observaciones: '' });

  const canEdit = ['gestion_general', 'administracion'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `0.5px solid ${theme.border}` };

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'prestadores', id), snap => {
      if (snap.exists()) { setPrestador({ id: snap.id, ...snap.data() }); setDatos({ ...snap.data() }); }
    });
    return unsub;
  }, [id]);

  useEffect(() => {
    const q = query(collection(db, 'prestadores', id, 'liquidaciones'), orderBy('creadoEn', 'desc'));
    const unsub = onSnapshot(q, snap => setLiquidaciones(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [id]);

  async function guardarCambios() {
    await updateDoc(doc(db, 'prestadores', id), datos);
    await registrarAccion(currentUser?.email, userRole, 'Prestadores', 'Editar prestador', prestador.nombre, clinicaId);
    setEditando(false);
  }

  async function crearLiquidacion() {
    if (!nuevaLiq.periodo) return;
    await addDoc(collection(db, 'prestadores', id, 'liquidaciones'), {
      ...nuevaLiq,
      cantidadTurnos: parseInt(nuevaLiq.cantidadTurnos) || 0,
      montoARS: parseFloat(nuevaLiq.montoARS) || 0,
      montoUSD: parseFloat(nuevaLiq.montoUSD) || 0,
      creadoEn: new Date(),
      creadoPor: currentUser?.email,
      clinicaId,
    });
    await registrarAccion(currentUser?.email, userRole, 'Prestadores', 'Nueva liquidación', `${prestador.nombre} — ${nuevaLiq.periodo}`, clinicaId);
    setModalLiquidacion(false);
    setNuevaLiq({ periodo: '', cantidadTurnos: '', montoARS: '', montoUSD: '', fechaPresentacion: '', fechaEstimadaCobro: '', estado: 'Pendiente presentación', observaciones: '' });
  }

  async function cambiarEstadoLiq(liqId, nuevoEstado) {
    const update = { estado: nuevoEstado };
    if (nuevoEstado === 'Cobrado') {
      update.fechaCobro = new Date();
      const liq = liquidaciones.find(l => l.id === liqId);
      if (liq?.montoARS > 0) {
        await addDoc(collection(db, 'caja'), {
          tipo: 'ingreso', categoria: 'Liquidación OS',
          detalle: `${prestador.nombre} — ${liq.periodo}`,
          monto: liq.montoARS, moneda: 'ARS', formaPago: 'Transferencia',
          fecha: Timestamp.fromDate(new Date()),
          registradoPor: currentUser?.email, rol: userRole,
          estado: 'confirmado', clinicaId,
        });
      }
    }
    await updateDoc(doc(db, 'prestadores', id, 'liquidaciones', liqId), update);
    await registrarAccion(currentUser?.email, userRole, 'Prestadores', `Liquidación ${nuevoEstado}`, prestador.nombre, clinicaId);
  }

  if (!prestador) return <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }}>Cargando...</div>;

  const pendienteCobro = liquidaciones.filter(l => l.estado !== 'Cobrado').reduce((a, l) => a + (l.montoARS || 0), 0);
  const estadoLiqColor = { 'Pendiente presentación': { color: theme.warning, bg: theme.warningBg }, 'Presentado': { color: theme.accent, bg: theme.infoBg }, 'Cobrado': { color: theme.success, bg: theme.successBg } };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo={prestador.nombre} backLabel="Prestadores" backRuta="/prestadores" />
      <div style={{ padding: '12px 24px 0', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: theme.bg }}>
        {canEdit && !editando && <button onClick={() => setEditando(true)} style={btnSecondaryStyle}>Editar</button>}
        {editando && <>
          <button onClick={guardarCambios} style={btnPrimaryStyle}>Guardar</button>
          <button onClick={() => { setEditando(false); setDatos(prestador); }} style={btnSecondaryStyle}>Cancelar</button>
        </>}
        {canEdit && <button onClick={() => setModalLiquidacion(true)} style={btnPrimaryStyle}>+ Liquidación</button>}
      </div>

      <div style={{ padding: '24px', background: theme.bg }}>
        {pendienteCobro > 0 && (
          <div style={{ background: theme.warningBg, border: `0.5px solid ${theme.warning}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: theme.warning, fontWeight: '500' }}>Saldo pendiente de cobro</span>
            <span style={{ fontSize: '18px', fontWeight: '700', color: theme.warning }}>${pendienteCobro.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
          </div>
        )}

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px', marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
            {[
              { label: 'TIPO', key: 'tipo' },
              { label: 'PRESENTACIÓN', key: 'tipoPresentacion' },
              { label: 'CONTACTO', key: 'contactoNombre' },
              { label: 'EMAIL', key: 'contactoEmail', type: 'email' },
              { label: 'TELÉFONO', key: 'contactoTelefono' },
            ].map(campo => (
              <div key={campo.key}>
                <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '4px', fontWeight: '500', letterSpacing: '1px' }}>{campo.label}</div>
                {editando
                  ? <input type={campo.type || 'text'} value={datos[campo.key] || ''} onChange={e => setDatos({...datos, [campo.key]: e.target.value})} style={inputStyle} />
                  : <div style={{ fontSize: '14px', color: theme.primary }}>{prestador[campo.key] || '—'}</div>
                }
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '4px', fontWeight: '500', letterSpacing: '1px' }}>PROCEDIMIENTO DE FACTURACIÓN</div>
              {editando
                ? <textarea value={datos.procedimientoFacturacion || ''} onChange={e => setDatos({...datos, procedimientoFacturacion: e.target.value})} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                : <div style={{ fontSize: '13px', color: theme.primary, lineHeight: '1.5' }}>{prestador.procedimientoFacturacion || '—'}</div>
              }
            </div>
          </div>
        </div>

        <div style={{ fontSize: '13px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>
          Liquidaciones ({liquidaciones.length})
        </div>

        {liquidaciones.length === 0
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '32px', fontSize: '13px', background: theme.bgCard, borderRadius: '10px', border: `0.5px solid ${theme.border}` }}>No hay liquidaciones registradas</div>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {liquidaciones.map(l => {
                const estadoCol = estadoLiqColor[l.estado] || { color: theme.secondary, bg: theme.bgCard };
                return (
                  <div key={l.id} style={{ background: theme.bgCard, borderRadius: '10px', padding: '14px 18px', border: `0.5px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{l.periodo}</div>
                      <div style={{ fontSize: '12px', color: theme.secondary }}>{l.cantidadTurnos} turnos · ${(l.montoARS || 0).toLocaleString('es-AR')}{l.montoUSD > 0 ? ` · USD ${l.montoUSD}` : ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: estadoCol.bg, color: estadoCol.color, fontWeight: '500' }}>{l.estado}</span>
                      {canEdit && l.estado !== 'Cobrado' && (
                        <select value={l.estado} onChange={e => cambiarEstadoLiq(l.id, e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: '11px' }}>
                          {ESTADOS_LIQ.map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
        }
      </div>

      {modalLiquidacion && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Nueva liquidación</span>
              <button onClick={() => setModalLiquidacion(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>PERÍODO *</label><input value={nuevaLiq.periodo} onChange={e => setNuevaLiq({...nuevaLiq, periodo: e.target.value})} placeholder="Ej: Enero 2026" style={inputStyle} /></div>
              <div><label style={labelStyle}>CANTIDAD DE TURNOS</label><input type="number" value={nuevaLiq.cantidadTurnos} onChange={e => setNuevaLiq({...nuevaLiq, cantidadTurnos: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>MONTO ARS</label><input type="number" value={nuevaLiq.montoARS} onChange={e => setNuevaLiq({...nuevaLiq, montoARS: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>MONTO USD</label><input type="number" value={nuevaLiq.montoUSD} onChange={e => setNuevaLiq({...nuevaLiq, montoUSD: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>FECHA PRESENTACIÓN</label><input type="date" value={nuevaLiq.fechaPresentacion} onChange={e => setNuevaLiq({...nuevaLiq, fechaPresentacion: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>FECHA EST. COBRO</label><input type="date" value={nuevaLiq.fechaEstimadaCobro} onChange={e => setNuevaLiq({...nuevaLiq, fechaEstimadaCobro: e.target.value})} style={inputStyle} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>ESTADO</label>
                <select value={nuevaLiq.estado} onChange={e => setNuevaLiq({...nuevaLiq, estado: e.target.value})} style={inputStyle}>
                  {ESTADOS_LIQ.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>OBSERVACIONES</label><textarea value={nuevaLiq.observaciones} onChange={e => setNuevaLiq({...nuevaLiq, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={crearLiquidacion} style={btnPrimaryStyle}>Guardar</button>
              <button onClick={() => setModalLiquidacion(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}